// =============================================================================
// MODEL THROTTLE POLICY — "latest first, downgrade under load", as DATA-IN-DB.
//
// docs/design/model-throttle-2026-07-21.md. The config pond behind the inference
// throttle: a user rides the LATEST model (most_current_anthropic default) while
// their rolling spend is under threshold; once they cross it ("hammering the
// system") their DEFAULT-TIER calls step DOWN the `ladder` (Opus -> Sonnet ->
// Haiku), one rung per threshold multiple, floored at the ladder's end.
//
// Mirrors rate_limit_policy.ts / usage_tier_allowance.ts byte-for-byte in spirit:
// a singleton-per-key table (one row per policy_key, UNIQUE so a re-seed upserts
// in place), the Source folds toward "no throttle" on ANY miss (absent row,
// inactive row, DB error) and NEVER throws — availability over strictness, a
// throttle must never be the thing that takes the product down.
//
// Config is DATA, NOT code: the window, the threshold, and the ladder all live
// in the row. No threshold literal in a .ts constant. A retune (or turning the
// whole throttle off) is a row UPDATE, never a deploy.
//
// The pure consumer (ClaudeService's throttle seam) never reads this table; it
// resolves through ModelThrottleSource (KV-cached over CACHE_KV, folds to null =
// no-op, never throws) and the resolved ladder feeds a PURE resolver.
//
// NO-INVENTION is untouched + model-agnostic: the throttle only picks WHICH model
// a default-tier call uses; the grounding gate + the 3 linters run AFTER the model
// and are model-agnostic, so whichever rung resolves, an invented value still
// cannot pass. Additive only (rule #11), applied via the Neon tool, mirrored here.
// =============================================================================

import { sql } from 'drizzle-orm';
import {
  boolean,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  uniqueIndex,
  varchar,
} from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** The policy-key keys this brief seeds. One key = one throttled tier. 'default_tier'
 *  governs the Opus/most_current_anthropic default. A future tier is just a new row —
 *  no shape change — so this union is documentation, not an enum constraint (matches
 *  rate_limit_policy.RateLimitRouteClass's same posture). */
export type ThrottlePolicyKey = 'default_tier';

/** RESERVED per-plan override shape (e.g. the `plan='founder'` ceiling, FOUNDER_CAP=100).
 *  Carried as DATA so a founder gets a much higher threshold WITHOUT a tier-specific code
 *  branch. NOT consumed by the resolver yet — flattening a plan override into the policy
 *  before the resolver runs is a future step (needs the caller's plan in hand at the seam);
 *  see the design doc's "Founder tier (noted, not built)". */
export interface ThrottlePlanOverride {
  thresholdMicroUsd?: number;
  windowHours?: number;
  ladder?: string[];
}

export const modelThrottlePolicy = pgTable(
  'model_throttle_policy',
  {
    id: id(),
    /** The policy key — 'default_tier' today. UNIQUE so a re-seed/recalibration
     *  upserts in place (ON CONFLICT) instead of duplicating. */
    policyKey: varchar('policy_key', { length: 48 }).notNull().default('default_tier'),
    /** Row toggle — an INACTIVE row is treated as absent by the Source (never
     *  silently applied), so the throttle no-ops and the latest model is served
     *  (matches rate_limit_policy.active's exact posture). */
    active: boolean('active').notNull().default(true),
    /** The rolling window the spend meter measures over, in hours. */
    windowHours: integer('window_hours').notNull().default(24),
    /** Spend ceiling per window, in MICRO-USD (matches ai_call_log.cost_micro_usd).
     *  The PRIMARY dimension: `steps = floor(spend / threshold_micro_usd)`. NULLABLE
     *  ("and/or threshold_output_tokens") — a null/<=0 threshold makes the resolver
     *  no-op (fail toward the latest model). */
    thresholdMicroUsd: integer('threshold_micro_usd'),
    /** RESERVED — a future output-token dimension of the same window. Not read by the
     *  resolver today (the cost meter is the live path); carried so a token-based
     *  throttle is a config change, not a schema change. */
    thresholdOutputTokens: integer('threshold_output_tokens'),
    /** The downgrade path: an ORDERED array of concrete model ids, top = latest
     *  (e.g. ['claude-opus-4-8','claude-sonnet-5','claude-haiku-4-5-20251001']).
     *  The requested model is expected to be ladder[0]; if it has drifted off the
     *  ladder the resolver starts at the top rung. Empty array → the resolver no-ops. */
    ladder: jsonb('ladder').$type<string[]>().notNull().default(sql`'[]'::jsonb`),
    /** RESERVED — per-plan overrides keyed by plan (e.g. { "founder": { thresholdMicroUsd } }).
     *  Carried as DATA for the founder-tier ceiling; NOT consumed by the resolver yet. */
    planOverrides: jsonb('plan_overrides').$type<Record<string, ThrottlePlanOverride>>(),
    /** Human-readable note on why this policy is set the way it is (optional). */
    notes: text('notes'),
    ...timestamps,
  },
  (t) => ({
    policyKeyIdx: index('model_throttle_policy_key_idx').on(t.policyKey),
    policyKeyUq: uniqueIndex('model_throttle_policy_key_uq').on(t.policyKey),
  }),
);
